import { ApiError } from "@/lib/apiClient";
import { observeApiExchange } from "@shared/serverReachability";

const DEFAULT_TIMEOUT_MS = 25_000;
const MAX_RETRIES = 2;
const RETRY_BASE_MS = 700;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function isRetriableError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.status === 0) return true;
  return err.status === 408 || err.status === 429 || err.status >= 502;
}

export type GuestTabletPublicFetchOptions = RequestInit & {
  timeoutMs?: number;
  retries?: number;
};

/**
 * Öffentliche Tablet-API mit Timeout und begrenzten Wiederholungen bei Netzwerkfehlern.
 */
export async function guestTabletPublicFetch(
  buildUrl: (path: string) => string,
  publicHeaders: () => HeadersInit,
  path: string,
  init?: GuestTabletPublicFetchOptions,
): Promise<Response> {
  const timeoutMs = init?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const maxRetries = init?.retries ?? MAX_RETRIES;
  const { timeoutMs: _t, retries: _r, ...requestInit } = init ?? {};

  let lastError: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(buildUrl(path), {
        ...requestInit,
        credentials: "omit",
        signal: controller.signal,
        headers: { ...publicHeaders(), ...(requestInit.headers || {}) },
      });
      observeApiExchange(res.status);
      return res;
    } catch (err) {
      const aborted = err instanceof DOMException && err.name === "AbortError";
      const message = aborted
        ? "Zeitüberschreitung – Server antwortet nicht rechtzeitig."
        : err instanceof Error &&
            (err.message === "Load failed" || err.message === "Failed to fetch")
          ? "Verbindung zum Server fehlgeschlagen."
          : err instanceof Error
            ? err.message
            : "Netzwerkfehler";
      lastError = new ApiError(0, message);
      if (!isRetriableError(lastError) || attempt === maxRetries) {
        observeApiExchange(null);
        throw lastError;
      }
      await sleep(RETRY_BASE_MS * (attempt + 1));
    } finally {
      window.clearTimeout(timer);
    }
  }
  throw lastError;
}
